import { useEffect, useState } from "react";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import {
  deliveryAddressReading,
  formatPriceOrNotYetPriced,
} from "@shared-core/domain/checkout-delivery";
import type {
  CustomerBasketArtworkSlot,
  CustomerBasketLine,
  CustomerBasketLineDelivery,
  CustomerBasketRemovedAddress,
} from "@shared-core/data/customer-basket-types";
import { addBackRequest } from "@shared-core/domain/customer-address-book";
import {
  addressArrivalSentences,
  arrivalSentence,
} from "@shared-core/domain/delivery-dates";
import { listVersions } from "@shared-core/domain/line-versions";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import { ChosenOptions } from "../../customer-pages/ChosenOptions";
import { ItemThumbnail } from "../../customer-pages/ItemThumbnail";
import { usePortalViewer } from "../PortalViewerContext";
import { ArtworkSlots } from "./ArtworkSlots";
import { BasketLineDeliveryDialog } from "./BasketLineDeliveryDialog";
import { AlertTriangle, Loader2, Pencil, Trash2 } from "lucide-react";

// One line of the basket: what it is, where it goes and when it arrives (with delivery dates on: each address with
// how many go there, and the date or window; where a split line's addresses arrive on different days each says its
// own and the line says its whole window; off, the one address and "usually dispatched within N working days"), its price ex VAT with delivery (its
// estimate priced alone, or "Not yet priced", never £0; none when its price includes delivery), the quantity
// (which re-prices when the person leaves the box), the files, and Remove. VAT and the totals are the summary
// panel's, once, beside the lines. A line the server says cannot be
// ordered online shows its reason in place of a price, blocks checkout, and offers Remove only. A line whose
// address the person took out of their address book (only while the book is the rule, doc 21 section 18) says so
// in the server's sentence on the line itself, and offers adding that address back or removing the item to choose
// another; where the line can be changed in place (`change_delivery_url`, delivery dates on) that button is Change
// delivery, which opens the dialog on the line itself and does not remove it. Where this build
// cannot change the basket (`canChange` false: the real build until PO-3) or the line names no URL for a change,
// that control is not drawn: the quantity is read, not edited, and there is no Remove or file button.
export function BasketLineCard({
  line,
  canChange,
  canChangeFiles,
  busy,
  isCheckingPrice = false,
  error,
  onSetQuantity,
  onRemove,
  onAddFile,
  onRemoveFile,
  onAddBackAddress,
  onChooseAnotherAddress,
  onDeliveryChanged,
}: {
  line: CustomerBasketLine;
  /** Quantity and Remove (the basket's own changes). */
  canChange: boolean;
  /** Add, replace and remove a file (its own group of routes). */
  canChangeFiles: boolean;
  busy: boolean;
  /** The page is asking for today's price now: a line with a price to confirm says it is being checked. */
  isCheckingPrice?: boolean;
  /** A refusal of this line's last change, in the server's words. */
  error: string | null;
  onSetQuantity: (line: CustomerBasketLine, quantity: number) => void;
  onRemove: (line: CustomerBasketLine) => void;
  onAddFile: (slot: CustomerBasketArtworkSlot, file: File) => Promise<void>;
  onRemoveFile: (slot: CustomerBasketArtworkSlot) => Promise<void>;
  /** Post the removed address back into the person's book (typing it again brings it back). */
  onAddBackAddress: (
    line: CustomerBasketLine,
    removed: CustomerBasketRemovedAddress,
  ) => void;
  /** Take the item out of the basket and go to its page, to add it again at another address (where it cannot be changed in place). */
  onChooseAnotherAddress: (line: CustomerBasketLine) => void;
  /** A change of delivery was saved: the basket is read again. */
  onDeliveryChanged: () => void;
}) {
  const [isChangingDelivery, setIsChangingDelivery] = useState(false);
  const [draftQuantity, setDraftQuantity] = useState(String(line.quantity));
  // A re-read that changes the quantity (a reorder, a change from elsewhere) replaces what was typed.
  useEffect(() => setDraftQuantity(String(line.quantity)), [line.quantity]);
  const { isStaffView, buildPortalUrl } = usePortalViewer();
  const unavailable = line.unavailable_reason !== null;
  // The line's date, or its window ("Expected Thu 27 – Fri 28 Oct") where it carries both ends. On a split line it is
  // the slowest address's, and the addresses say their own only where they differ.
  const expectedArrival = line.expected_arrival_on
    ? arrivalSentence(line.expected_arrival_on, line.expected_arrival_from_on)
    : null;
  const addressArrivals = addressArrivalSentences(line.deliveries);
  // A line of several versions keeps its quantities: changing the versions goes back through the item page.
  const versionsListed = listVersions(line.versions ?? []);
  // A split line's quantity is its rows' sum, changed with its addresses in Change delivery.
  const isChangedWithItsAddresses =
    line.delivery_address_count > 1 && line.actions.change_delivery_url != null;
  const canSetQuantity =
    canChange &&
    line.actions.set_quantity_url !== null &&
    !versionsListed &&
    !isChangedWithItsAddresses;
  const canRemove = canChange && line.actions.remove_url !== null;
  // Named only while delivery dates are on, for a line that can be ordered; the server decides, the page only reads it.
  const canChangeDelivery =
    canChange &&
    !unavailable &&
    line.actions.change_delivery_url != null &&
    line.actions.preview_delivery_url != null;
  const quantityErrorId = `quantity-error-${line.line_id}`;

  function commitQuantity() {
    const quantity = Number(draftQuantity);
    if (draftQuantity.trim() === "" || quantity === line.quantity) {
      setDraftQuantity(String(line.quantity));
      return;
    }
    onSetQuantity(line, quantity);
  }

  return (
    <Card className="gap-3 p-4" aria-label={line.title}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <ItemThumbnail name={line.title} family={line.spec} />
          <div className="min-w-0 space-y-0.5">
            {isStaffView ? (
              // The catalogue is the customer's own (staff reach it only while acting), so staff read the title.
              <p className="font-semibold">{line.title}</p>
            ) : (
              <Link
                to={paths.portalCatalogueItem({ itemId: line.item_id })}
                className="font-semibold hover:text-primary"
              >
                {line.title}
              </Link>
            )}
            {line.spec && (
              <p className="text-sm text-muted-foreground">{line.spec}</p>
            )}
            <ChosenOptions options={line.chosen_options} />
            {versionsListed && (
              <p
                className="text-sm tabular-nums"
                data-testid="basket-line-versions"
              >
                {versionsListed}
              </p>
            )}
            {line.deliveries.length > 0 ? (
              <DeliveryList
                deliveries={line.deliveries}
                arrivals={addressArrivals}
              />
            ) : (
              line.delivery_address_name && (
                <p className="text-sm text-muted-foreground">
                  Delivering to{" "}
                  {deliveryAddressReading(
                    line.delivery_address_name,
                    line.delivery_address_count,
                  )}
                </p>
              )
            )}
            {!unavailable && expectedArrival && (
              <p className="text-sm font-medium">{expectedArrival}</p>
            )}
            {!unavailable && line.dispatch_note && (
              <p className="text-sm text-muted-foreground">
                {line.dispatch_note}
              </p>
            )}
          </div>
        </div>
        {(canChangeDelivery || canRemove) && (
          <div className="flex flex-wrap items-center gap-1">
            {canChangeDelivery && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setIsChangingDelivery(true)}
                aria-label={`Change delivery for ${line.title}`}
              >
                <Pencil />
                Change delivery
              </Button>
            )}
            {canRemove && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onRemove(line)}
                aria-label={`Remove ${line.title}`}
              >
                <Trash2 />
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      {canChangeDelivery && (
        <BasketLineDeliveryDialog
          line={line}
          open={isChangingDelivery}
          onOpenChange={setIsChangingDelivery}
          onChanged={onDeliveryChanged}
        />
      )}

      {!unavailable && line.address_removed_from_book !== null && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{line.address_removed_from_book}</p>
            {canChange && (
              <div className="flex flex-wrap gap-2">
                {line.removed_addresses
                  .filter((removed) => addBackRequest(removed) !== null)
                  .map((removed) => (
                    <Button
                      key={removed.address_id}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => onAddBackAddress(line, removed)}
                    >
                      Add {removed.name} back to my address book
                    </Button>
                  ))}
                {canChangeDelivery ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setIsChangingDelivery(true)}
                  >
                    Change delivery
                  </Button>
                ) : (
                  canRemove && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => onChooseAnotherAddress(line)}
                    >
                      Remove and choose another address
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {unavailable ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>{line.unavailable_reason}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            {canSetQuantity ? (
              <div className="space-y-1.5">
                <Label htmlFor={`quantity-${line.line_id}`}>Quantity</Label>
                <Input
                  id={`quantity-${line.line_id}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  className="w-32 tabular-nums"
                  value={draftQuantity}
                  disabled={busy}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? quantityErrorId : undefined}
                  onChange={(event) => setDraftQuantity(event.target.value)}
                  onBlur={commitQuantity}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur();
                  }}
                />
              </div>
            ) : (
              <div className="space-y-0.5 text-sm">
                <p>
                  Quantity{" "}
                  <span className="font-medium tabular-nums">
                    {line.quantity.toLocaleString("en-GB")}
                  </span>
                </p>
                {versionsListed && canChange && (
                  <p className="text-xs text-muted-foreground">
                    To change the versions, add the item again from its page.
                  </p>
                )}
                {isChangedWithItsAddresses && canChange && (
                  <p className="text-xs text-muted-foreground">
                    To change how many go to each address, use Change delivery.
                  </p>
                )}
              </div>
            )}
            <dl className="space-y-0.5 text-right text-sm tabular-nums">
              <div className="flex justify-end gap-4">
                <dt className="text-muted-foreground">Price (ex VAT)</dt>
                <dd className="font-medium">
                  {formatQuotedPrice(line.goods_pence)}
                </dd>
              </div>
              {(line.delivery_price_pence !== null ||
                line.is_delivery_not_yet_priced) && (
                <div className="flex justify-end gap-4">
                  <dt className="text-muted-foreground">Delivery (ex VAT)</dt>
                  <dd>
                    {formatPriceOrNotYetPriced(
                      line.is_delivery_not_yet_priced
                        ? null
                        : line.delivery_price_pence,
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>
          {line.price_status === "to_confirm" && (
            <div
              className="flex flex-wrap items-center justify-end gap-2 text-right text-sm"
              data-testid="basket-line-price-to-confirm"
              aria-busy={isCheckingPrice}
            >
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200">
                {isCheckingPrice && <Loader2 className="size-3 animate-spin" />}
                Price to confirm
              </span>
              {line.price_note && (
                <span className="text-muted-foreground">{line.price_note}</span>
              )}
            </div>
          )}
          {error && (
            <p
              id={quantityErrorId}
              role="alert"
              className="text-sm text-red-700 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <ArtworkSlots
            slots={line.artwork_slots}
            readOnly={!canChangeFiles}
            disabled={busy}
            onAddFile={onAddFile}
            onRemoveFile={onRemoveFile}
            checkResultTo={(checkId) =>
              buildPortalUrl(paths.portalArtworkCheckResult({ checkId }))
            }
          />
        </>
      )}
    </Card>
  );
}

// Where a line goes: one address by name, or each address of a split line with how many go there. The postcode
// tells apart two depots of one name; a quantity is shown only where it is not simply the whole line's.
function DeliveryList({
  deliveries,
  arrivals,
}: {
  deliveries: CustomerBasketLineDelivery[];
  /** Each address's own arrival, where the addresses differ; null where the line's date says it once. */
  arrivals: string[] | null;
}) {
  const [only] = deliveries;
  if (deliveries.length === 1 && only) {
    return (
      <p className="text-sm text-muted-foreground">
        Delivering to {placeOf(only)}
      </p>
    );
  }
  return (
    <div className="text-sm text-muted-foreground">
      <p>Delivering to {deliveries.length} addresses</p>
      <ul className="mt-0.5 space-y-0.5" aria-label="Delivery addresses">
        {deliveries.map((delivery, index) => (
          <li key={index} className="flex flex-wrap gap-x-2 tabular-nums">
            <span className="min-w-0">{placeOf(delivery)}</span>
            <span className="font-medium text-foreground">
              × {delivery.quantity.toLocaleString("en-GB")}
            </span>
            {arrivals?.[index] && (
              <span className="text-foreground">{arrivals[index]}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function placeOf(delivery: CustomerBasketLineDelivery): string {
  return [delivery.address_name, delivery.postcode].filter(Boolean).join(", ");
}
